'use client';

import Image from 'next/image';
import React from 'react';
import { animate } from '@motionone/dom';
import { MOTION_DURATION, MOTION_EASING, prefersReducedMotion } from '@/lib/motion-one';

type HeroCircleImageProps = {
  src: string;
  alt: string;
  title?: string;
  width: number;
  height: number;
  sizes: string;
  priority?: boolean;
};

function HeroLoader() {
  return (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center overflow-hidden rounded-full bg-[#15192b]"
      aria-hidden="true"
    >
      <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-[#1e2235] via-[#2a3150]/80 to-[#1e2235]" />
      <div
        className="absolute inset-6 rounded-full border border-[#64b5f6]/20 motion-safe:animate-pulse"
        style={{ animationDuration: '2.4s' }}
      />
      <div className="relative h-10 w-10 rounded-full border-2 border-white/15 border-t-[#ffb74d] motion-safe:animate-spin" />
    </div>
  );
}

export function HeroCircleImage({
  src,
  alt,
  title,
  width,
  height,
  sizes,
  priority = false,
}: HeroCircleImageProps) {
  const [loaded, setLoaded] = React.useState(false);
  const frameRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setLoaded(false);
  }, [src]);

  React.useEffect(() => {
    if (!loaded || !frameRef.current) return;
    const el = frameRef.current;
    if (prefersReducedMotion()) {
      el.style.opacity = '1';
      el.style.transform = 'scale(1)';
      return;
    }
    el.style.opacity = '0';
    el.style.transform = 'scale(0.94)';
    animate(
      el,
      { opacity: 1, transform: 'scale(1)' },
      { duration: MOTION_DURATION.slow, easing: MOTION_EASING },
    );
  }, [loaded]);

  return (
    <div
      ref={frameRef}
      className="absolute inset-0 overflow-hidden rounded-full border border-white/10 shadow-[0_0_60px_rgba(100,181,246,0.15)]"
    >
      {!loaded ? <HeroLoader /> : null}
      <Image
        src={src}
        alt={alt}
        title={title}
        width={width}
        height={height}
        sizes={sizes}
        priority={priority}
        fetchPriority={priority ? 'high' : undefined}
        className={`h-full w-full object-cover transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
      <div
        className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-b from-transparent via-transparent to-[#0b0d19]/60"
        aria-hidden="true"
      />
    </div>
  );
}
